import React, { useState, useMemo, useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { Stage as StageData } from '@silvertours/common-landingpages-view';
import { HostLegacy, LayoutLegacy } from '@silvertours/front-legacy-entities';
import { MediaLegacy, Theme } from '@silvertours/front-legacy-shared';
import { useMedia } from 'react-use';
import { SearchForm, SearchProvider } from '../SearchFormLegacy';
import {
  StageContentContainer,
  StageLink,
  StageWrapper,
  StyledH1,
  WizardSearchWrapper,
} from './Stage.styles';
import { StageContext } from './context';
import { SearchContainer } from './SearchContainer';

type StageProps = {
  content?: StageData;
  // TODO: Provide link from BFF
  link?: {
    href: string;
    target: string;
    title: string;
  };
  headline?: string;
  isMinimal?: boolean;
  isPlain?: boolean;
  isErrorPage?: boolean;
};

const IMAGE_PATH_PREFIX = '/bm/global/files/images/';

const Stage = ({
  content,
  link,
  headline,
  isMinimal,
  isPlain,
  isErrorPage,
}: StageProps) => {
  const isMobile = useMedia(
    `(max-width: ${Theme.getBreakpoint('sm')}px)`,
    true,
  );

  const {
    keyword,
    search,
    image: imageData,
    includeSearch,
    imageWithFocalPoint,
  } = content || {};

  const t = useTranslations('features.stageLegacy.stage');

  const { formatImage } = HostLegacy.useHost();

  const defaultImage: MediaLegacy.ImageProps = {
    src: t('defaultImage.src'),
    alt: t('defaultImage.alt'),
    id: t('defaultImage.id'),
  };

  const image = imageWithFocalPoint || imageData || defaultImage;

  const [stageImage, setStageImage] = useState(image.src);

  const handleStageImageChange = useCallback(
    (src?: string) => {
      const imageSrc = src ? `${IMAGE_PATH_PREFIX}${src}` : defaultImage.src;
      setStageImage(imageSrc);
    },
    [defaultImage.src],
  );

  const value = useMemo(
    () => ({
      stageImage,
      setStageImage: handleStageImageChange,
    }),
    [handleStageImageChange, stageImage],
  );

  const newImage = { ...image };

  if (image === defaultImage) {
    newImage.src = formatImage(stageImage || image.src);
  }

  return (
    <StageWrapper isMinimal={isMinimal} isErrorPage={isErrorPage}>
      <LayoutLegacy.ImageSegment
        id="stage-image"
        image={newImage}
        isStage
        includeSearch={includeSearch}
        isMinimal={isMinimal}
        isPlain={isPlain}
        isErrorPage={isErrorPage}
      >
        <StageContentContainer isMinimal={isMinimal} isErrorPage={isErrorPage}>
          {isMobile && !isMinimal && (
            <WizardSearchWrapper>
              {!isErrorPage && <StyledH1>{headline}</StyledH1>}
              <SearchProvider pageData={search}>
                <SearchContainer
                  image={newImage}
                  includeSearch={includeSearch}
                  isErrorPage={isErrorPage}
                />
              </SearchProvider>
            </WizardSearchWrapper>
          )}
          {!isMobile && (
            <StageContext.Provider value={value}>
              {Boolean(link?.href) && (
                <StageLink href={link?.href} target={link?.target}>
                  {link?.title}
                </StageLink>
              )}
              {includeSearch && (
                <SearchForm
                  stageTitle={keyword}
                  pageData={search}
                  isErrorPage={isErrorPage}
                  isWide
                />
              )}
            </StageContext.Provider>
          )}
        </StageContentContainer>
      </LayoutLegacy.ImageSegment>
    </StageWrapper>
  );
};

export { Stage };
export type { StageProps };
